import { useSelector } from "react-redux";
import type { RootState } from "../stores/store";
import {
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
} from "./common/authentification-functions";

/**
 * Reactive permission checks for actions and UI parts (buttons, tabs, widgets).
 *
 * Permissions are read straight from the JWT (the single source of truth), so a
 * decision is inherently scoped to the one tenant the token was issued for. The
 * `useSelector` on `userState` is only a re-render trigger — it re-evaluates the
 * checks on login/logout/token refresh (same pattern as TenantProvider).
 *
 * This is the action-level axis: it checks ONLY permission, never a feature flag.
 * Page/route availability (feature flag + permission) is handled by the route guards.
 */
export function useCan() {
  useSelector((state: RootState) => state.user.userState);

  return {
    can: (permission: number) => hasPermission(permission),
    canAny: (permissions: number[]) => hasAnyPermission(permissions),
    canAll: (permissions: number[]) => hasAllPermissions(permissions),
  };
}